import React from "react";
import BNBLogo from "assets/images/bnb_logo.svg";
import DGNBLogo from "assets/images/dgnb_logo.svg";
import LevelSLogo from "assets/images/levels_logo.svg";
import QNGLogo from "assets/images/qng_logo.svg";
import Alert from "components/Alert";
import { LEVELS_DOC_URL } from "utils";
import { FormValue, buildingSettingsStore } from "./BuildingSettingsStore";
import BNB from "./components/BNB";
import { useTranslation } from "react-i18next";
import DGNB18 from "./components/DGNB18";
import CalculationLogic from "./components/CalculationLogic";
import DGNB23AndQNG from "./components/DGNB23AndQNG";
import { BuildingCertificationSettings } from "api-client";
import { useCertificationLicense } from "hooks/useCertificationLicense";

export interface CertificationProps {
  id: string;
  checked: boolean | undefined | null;
  logo: string;
  name: string;
  content: React.ReactNode;
}

const useCertificationsList = (
  handleChange?: (
    certification: keyof BuildingCertificationSettings,
    value: FormValue,
    name?: string
  ) => void
) => {
  const { t } = useTranslation();
  const { settings, showMandatoryAlert } = buildingSettingsStore;
  const { isAvailable } = useCertificationLicense();

  const certifications: CertificationProps[] = [
    {
      id: "bnb",
      checked: settings.bnb?.is_active,
      logo: BNBLogo,
      name: t("buildingSettings.bnbCertificateName"),
      content: (
        <>
          <BNB
            netFloorArea={settings?.bnb?.net_floor_area}
            onChange={(value, name) => handleChange?.("bnb", value, name)}
            hasError={Boolean(
              showMandatoryAlert && !settings.bnb?.net_floor_area
            )}
          />
          <CalculationLogic type="bnb" />
        </>
      ),
    },
    {
      id: "dgnb18",
      checked: settings.dgnb18?.is_active,
      logo: DGNBLogo,
      name: "DGNB Version 2018",
      content: (
        <>
          <DGNB18
            tgaFactor={settings?.dgnb18?.reduce_tga_factor_to_1_1}
            relevantArea={settings?.dgnb18?.relevant_area}
            onChange={(value, name) => handleChange?.("dgnb18", value, name)}
            hasError={Boolean(
              showMandatoryAlert && !settings.dgnb18?.relevant_area
            )}
          />
          <CalculationLogic type="dgnb18" className="border-t" />
          <Alert
            type="info"
            description={t("buildingSettings.DGNB18Alert")}
            className=" border-none py-0 !bg-gray-50 pb-4"
          />
        </>
      ),
    },
    {
      id: "dgnb23",
      checked: settings.dgnb23?.is_active,
      logo: DGNBLogo,
      name: "DGNB Version 2023",
      content: (
        <>
          <DGNB23AndQNG
            buildingType={settings.dgnb23?.building_type}
            lcaClass={settings.dgnb23?.lca_class}
            energeticStandard={settings?.dgnb23?.energetic_standard}
            levelOfRequirements={settings?.dgnb23?.level_of_requirements}
            onChange={(value, name) => handleChange?.("dgnb23", value, name)}
            hasError={Boolean(showMandatoryAlert && settings.dgnb23?.is_active)}
          />
          <CalculationLogic type="dgnb23" className="border-t" />
        </>
      ),
    },
    {
      id: "levels",
      checked: settings.levels?.is_active,
      logo: LevelSLogo,
      name: "Level(s)",
      content: (
        <>
          <CalculationLogic type="levels" />
          <Alert
            type="info"
            description={
              <div
                dangerouslySetInnerHTML={{
                  __html: t("buildingSettings.levelsAlert", {
                    link: LEVELS_DOC_URL,
                  }),
                }}
              ></div>
            }
            className=" border-none py-0 !bg-gray-50 pb-4"
          />
        </>
      ),
    },
    {
      id: "qng",
      checked: settings.qng?.is_active,
      logo: QNGLogo,
      name: t("buildingSettings.qngCetificationName"),
      content: (
        <>
          <DGNB23AndQNG
            buildingType={settings?.qng?.building_type}
            lcaClass={settings?.qng?.lca_class}
            energeticStandard={settings?.qng?.energetic_standard}
            levelOfRequirements={settings?.qng?.level_of_requirements}
            onChange={(value, name) => handleChange?.("qng", value, name)}
            hasError={Boolean(showMandatoryAlert && settings.qng?.is_active)}
          />
          <CalculationLogic type="qng" className="border-t" />
        </>
      ),
    },
  ];

  // Tier 1: only show certification systems the organisation license entitles.
  return {
    certifications: certifications.filter((item) => isAvailable(item.id)),
  };
};

export default useCertificationsList;
